/**
 * §14.8 screen 1 — Boshqaruv paneli.
 *
 * Reads `GET /admin/summary` (through the shell's single 60 s poll),
 * `GET /admin/stats/mahallas` and `GET /admin/stats/timeseries?bucket=day`.
 *
 * ===========================================================================
 * THE TWO SLA NUMBERS ARE RENDERED AS TWO NUMBERS WITH TWO LABELS.
 * ===========================================================================
 *
 * The reference product shows one "SLA %" tile against a 5-day target. Ours
 * shows two, because they answer two different questions. `statutory_on_time_pct`
 * is compliance with F-6 Art. 28 — 15 days, plus any lawful extension.
 * `target_on_time_pct` is the hokimlik's own 5-day commitment (owner ruling,
 * 2026-09-28), the date the citizen was given. Since the two windows differ, the
 * two percentages usually differ too, and a hokim needs to know which is which:
 * §14.2.1 keeps them in separate response fields, this screen keeps them in
 * separate tiles, and each caption names the window its percentage is measured
 * against. One merged number would be either an invented legal requirement or an
 * overstated promise.
 *
 * ===========================================================================
 * AN ABSENT NUMBER RENDERS AS AN EM DASH, NEVER AS A ZERO.
 * ===========================================================================
 *
 * The endpoints omit a ratio that has no denominator. `formatMetric` turns that
 * into "—". "0 %" where the truth is "nothing has been closed yet" is a false
 * claim, and it is the kind that gets repeated in a meeting.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MUROJAAT_STATUSES } from '@navoiy/domain';
import type { components } from '@navoiy/api-types';
import { ApiError, api, errorKey, jsonBody, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { AppellantMonogram } from '../components/AppellantMonogram.jsx';
import {
  BarChart,
  CLOSED_COLOR,
  Donut,
  RECEIVED_COLOR,
  STATUS_COLOR,
  Sparkline,
} from '../components/charts/index.jsx';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { formatDate, formatDateTime, formatMetric, formatPercent } from '../lib/format.js';
import { useSummary } from '../lib/useSummary.js';
import {
  Badge,
  Card,
  EmptyState,
  ErrorBanner,
  Spinner,
  Table,
  Td,
  Th,
  Tr,
} from '../ui/index.jsx';

type MahallaStats = components['schemas']['AdminMahallaStatsResponse'];
type Timeseries = components['schemas']['AdminTimeseriesResponse'];

/** How many mahallas the dashboard's bar chart shows before the full screen. */
const DASHBOARD_BARS = 10;

function Kpi({
  label,
  value,
  caption,
  tone,
}: {
  label: string;
  value: string;
  caption?: string;
  tone?: 'danger' | 'warn';
}): React.JSX.Element {
  const valueTone =
    tone === 'danger' ? 'text-danger-500' : tone === 'warn' ? 'text-gold-700' : 'text-brand-900';
  return (
    /*
     * `min-w-0` and `break-words` are load-bearing, not defensive.
     *
     * A grid item's default `min-width: auto` refuses to shrink below its
     * longest unbreakable word, so one long KPI label pushes the whole strip
     * wider than its container and the rightmost tiles are clipped off the
     * screen. Four locales make that a certainty rather than a risk: the same
     * caption is one word in English and three in Russian.
     */
    <div className="min-w-0 rounded-tile border-1 border-line-200 bg-surface-0 px-4 py-3 shadow-elev-1">
      <p className="break-words text-xs font-semibold uppercase tracking-label text-ink-500">
        {label}
      </p>
      <p className={`text-2xl font-bold tabular-nums ${valueTone}`}>{value}</p>
      {caption ? <p className="break-words text-xs leading-snug text-ink-400">{caption}</p> : null}
    </div>
  );
}

export function DashboardScreen(): React.JSX.Element {
  const { t, locale } = useI18n();
  const { expire } = useSession();
  const navigate = useNavigate();
  const { summary, loading, error: summaryError } = useSummary();

  const [mahallas, setMahallas] = useState<MahallaStats | null>(null);
  const [trend, setTrend] = useState<Timeseries | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [mahallaStats, timeseries] = await Promise.all([
        api().GET('/admin/stats/mahallas', { params: { query: { locale } } }),
        api().GET('/admin/stats/timeseries', { params: { query: { bucket: 'day' } } }),
      ]);
      setMahallas(jsonBody(unwrap(mahallaStats)));
      setTrend(jsonBody(unwrap(timeseries)));
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        expire();
        return;
      }
      const { key, values } = errorKey(caught);
      setError(t(key, values));
    }
  }, [locale, expire, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !summary) return <Spinner label={t('admin.common.loading')} />;
  if (!summary) {
    return <ErrorBanner message={error ?? t('admin.error.generic')} />;
  }

  const { kpi } = summary;
  const bars = (mahallas?.items ?? []).slice(0, DASHBOARD_BARS);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto text-lg font-bold text-brand-900">{t('admin.dashboard.title')}</h1>
        {/*
          §14.1 — a scoped total MUST say so. Without this badge a handler
          restricted to three mahallas reads `total` as the city's figure, and
          every number on this screen is then wrong by an unknown factor.
        */}
        {summary.scoped ? <Badge tone="gold">{t('admin.scoped.label')}</Badge> : null}
        {summaryError ? <Badge tone="danger">{t('admin.topbar.staleData')}</Badge> : null}
      </div>

      <ErrorBanner message={error} />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6">
        <Kpi label={t('admin.dashboard.kpi.today')} value={String(kpi.today)} />
        <Kpi label={t('admin.dashboard.kpi.total')} value={String(kpi.total)} />
        <Kpi label={t('admin.dashboard.kpi.inProgress')} value={String(kpi.in_progress)} />
        <Kpi
          label={t('admin.dashboard.kpi.completed')}
          value={String(kpi.completed)}
          caption={t('admin.dashboard.kpi.completionPct', {
            pct: formatPercent(kpi.completion_pct),
          })}
        />
        <Kpi
          label={t('admin.dashboard.kpi.unassigned')}
          value={String(kpi.unassigned)}
          tone={kpi.unassigned > 0 ? 'warn' : undefined}
        />
        <Kpi
          label={t('admin.dashboard.kpi.overdue')}
          value={String(kpi.overdue)}
          tone={kpi.overdue > 0 ? 'danger' : undefined}
        />
        <Kpi label={t('admin.dashboard.kpi.dueSoon')} value={String(kpi.due_soon)} />
        <Kpi
          label={t('admin.dashboard.kpi.repeatAppellants')}
          value={String(kpi.repeat_appellants)}
          caption={t('admin.dashboard.kpi.repeatHint')}
        />
        <Kpi
          label={t('admin.dashboard.kpi.avgResolution')}
          value={formatMetric(kpi.avg_resolution_days)}
          caption={t('admin.dashboard.kpi.medianResolution', {
            days: formatMetric(kpi.median_resolution_days),
          })}
        />
        {/* The legal number and the internal target, side by side and never merged. */}
        <Kpi
          label={t('admin.dashboard.kpi.statutoryOnTime')}
          value={formatPercent(kpi.statutory_on_time_pct)}
          caption={t('admin.dashboard.kpi.statutoryHint')}
        />
        <Kpi
          label={t('admin.dashboard.kpi.targetOnTime')}
          value={formatPercent(kpi.target_on_time_pct)}
          caption={t('admin.dashboard.kpi.targetHint', { days: kpi.target_days })}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <Card title={t('admin.dashboard.statusTitle')}>
          <Donut
            label={t('admin.dashboard.statusTitle')}
            slices={MUROJAAT_STATUSES.map((status) => ({
              key: status,
              label: t(`shared.murojaat.status.${status}`),
              value: summary.status_breakdown[status],
              color: STATUS_COLOR[status],
            }))}
            centerValue={String(kpi.total)}
            centerLabel={t('admin.dashboard.kpi.total')}
            emptyLabel={t('admin.chart.empty')}
          />
        </Card>

        <Card title={t('admin.dashboard.trendTitle')}>
          <Sparkline
            label={t('admin.dashboard.trendTitle')}
            series={[
              {
                key: 'received',
                label: t('admin.dashboard.trendReceived'),
                points: (trend?.items ?? []).map((point) => point.received),
                color: RECEIVED_COLOR,
              },
              {
                key: 'closed',
                label: t('admin.dashboard.trendClosed'),
                points: (trend?.items ?? []).map((point) => point.closed),
                color: CLOSED_COLOR,
              },
            ]}
            emptyLabel={t('admin.chart.empty')}
          />
          {trend ? (
            <p className="pt-2 text-xs text-ink-400">
              {t('admin.dashboard.trendRange', {
                from: formatDate(trend.from),
                to: formatDate(trend.to),
              })}
            </p>
          ) : null}
        </Card>

        <Card
          title={t('admin.dashboard.mahallasTitle')}
          actions={
            summary.top_mahalla ? (
              <span className="text-xs text-ink-500">
                {t('admin.dashboard.topMahalla', {
                  name: summary.top_mahalla.name,
                  count: summary.top_mahalla.count,
                })}
              </span>
            ) : null
          }
        >
          <BarChart
            label={t('admin.dashboard.mahallasTitle')}
            items={bars.map((item) => ({
              key: item.mahalla_id,
              label: item.mahalla_name,
              value: item.total,
            }))}
            emptyLabel={t('admin.chart.empty')}
          />
        </Card>

        {/* -- the attention centre --------------------------------------- */}
        <Card title={t('admin.dashboard.attentionTitle')}>
          <p className="pb-3 text-sm text-ink-700">
            {t('admin.dashboard.attentionUnassigned', {
              count: summary.attention.unassigned_count,
            })}
          </p>
          <h3 className="pb-1 text-xs font-semibold uppercase tracking-label text-ink-500">
            {t('admin.dashboard.attentionOverdue')}
          </h3>
          {summary.attention.overdue.length === 0 ? (
            <EmptyState message={t('admin.dashboard.attentionEmpty')} />
          ) : (
            <ul className="flex flex-col">
              {summary.attention.overdue.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/murojaats/${item.id}`)}
                    className="flex w-full items-center gap-3 border-b-1 border-line-100 py-2 text-left text-sm last:border-b-0 hover:bg-surface-50"
                  >
                    <span className="font-semibold tracking-ref text-brand-800">
                      {item.reference}
                    </span>
                    <span className="truncate text-ink-500">{item.mahalla_name}</span>
                    <span className="ml-auto shrink-0 font-semibold text-danger-500">
                      {formatDate(item.due_at)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/*
            §14.2.1 round B — the repeat-appellant rows.

            The block is ABSENT, not empty, for a session without
            `appellant.list`: the API omits `attention.repeat` entirely for a
            `viewer`, and an empty list rendered under a heading would read as
            "nobody has come back" — a different and false statement from "you
            may not see who they are". The COUNT is in the KPI strip above,
            which every `analytics.read` session gets.
          */}
          {summary.attention.repeat ? (
            <>
              <h3 className="pb-1 pt-4 text-xs font-semibold uppercase tracking-label text-ink-500">
                {t('admin.dashboard.attentionRepeat')}
              </h3>
              {summary.attention.repeat.length === 0 ? (
                <EmptyState message={t('admin.dashboard.attentionRepeatEmpty')} />
              ) : (
                <ul className="flex flex-col">
                  {summary.attention.repeat.map((person) => (
                    <li key={person.appellant_id}>
                      <button
                        type="button"
                        onClick={() => navigate(`/appellants/${person.appellant_id}`)}
                        className="flex w-full items-center gap-3 border-b-1 border-line-100 py-2 text-left text-sm last:border-b-0 hover:bg-surface-50"
                      >
                        <AppellantMonogram name={person.display_name} />
                        <span className="truncate text-ink-900">
                          {person.display_name ?? t('admin.appellants.noName')}
                        </span>
                        <span className="whitespace-nowrap text-ink-500">
                          ···{person.phone_tail}
                        </span>
                        <span className="ml-auto shrink-0 font-semibold text-gold-700">
                          {t('admin.appellants.appealCountShort', {
                            count: person.appeal_count,
                          })}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : null}
        </Card>
      </div>

      <Card title={t('admin.dashboard.recentTitle')}>
        {summary.recent.length === 0 ? (
          <EmptyState message={t('admin.dashboard.recentEmpty')} />
        ) : (
          <Table
            head={
              <tr>
                <Th>{t('admin.column.reference')}</Th>
                <Th>{t('admin.column.mahalla')}</Th>
                <Th>{t('admin.column.kind')}</Th>
                <Th>{t('admin.column.channel')}</Th>
                <Th>{t('admin.column.received')}</Th>
                <Th>{t('admin.column.status')}</Th>
              </tr>
            }
          >
            {summary.recent.map((item) => (
              <Tr key={item.id} onClick={() => navigate(`/murojaats/${item.id}`)}>
                <Td className="whitespace-nowrap font-semibold tracking-ref text-brand-800">
                  {item.reference}
                </Td>
                <Td>{item.mahalla_name}</Td>
                <Td>{t(`shared.murojaat.kind.${item.kind}`)}</Td>
                <Td>{t(`admin.channel.${item.channel}`)}</Td>
                <Td className="whitespace-nowrap">{formatDateTime(item.received_at)}</Td>
                <Td>
                  <Badge tone={item.is_overdue ? 'danger' : 'neutral'}>
                    {t(`shared.murojaat.status.${item.status}`)}
                  </Badge>
                </Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
